import { useCallback, useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

// Shared inline, edit-in-place message editor (#1640). Used by both the Release
// Center (admin edits a broadcast message) and Support Chat (admin edits a DM),
// so the two stay consistent. It replaces the message text in the message column
// like Slack — full width, auto-growing plain-text textarea, on-brand emerald
// Save + neutral Cancel below it. Messages render as plain text, so there is no
// rich-text toolbar.
//
// The parent owns the editing state and mounts this in place of the rendered
// message; `onClose` returns to that rendered message (Cancel / Esc / a no-op
// save / a successful save). `onSave` persists the new content and throws on
// failure, in which case the editor stays open so the edit isn't lost.
export default function MessageEditor({
  content,
  onSave,
  onClose,
  className,
  saveLabel = 'Save',
}: {
  content: string;
  onSave: (next: string) => Promise<void>;
  onClose: () => void;
  className?: string;
  saveLabel?: string;
}) {
  const [saving, setSaving] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Grow the textarea to fit its content, letting the CSS max-height cap it and
  // scroll past that. Runs on mount and on every input.
  const autoSize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) {
      return;
    }
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  // On mount: size to fit, focus, and place the cursor at the end so the admin
  // can start typing immediately.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) {
      return;
    }
    autoSize();
    el.focus();
    const end = el.value.length;
    el.setSelectionRange(end, end);
  }, [autoSize]);

  const submit = useCallback(async () => {
    if (saving) {
      return;
    }

    const next = textareaRef.current?.value?.trim();

    // No-op when empty or unchanged: just return to the message, don't hit the API.
    if (!next || next === content.trim()) {
      onClose();
      return;
    }

    setSaving(true);
    try {
      await onSave(next);
    } catch {
      // Keep the editor open on failure so the edit isn't lost.
      setSaving(false);
      return;
    }
    onClose();
  }, [saving, content, onSave, onClose]);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      } else if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
        // Cmd/Ctrl+Enter saves; a plain Enter stays a newline.
        event.preventDefault();
        submit();
      }
    },
    [onClose, submit],
  );

  return (
    <form
      className={twMerge('flex w-full flex-col gap-2', className)}
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <textarea
        ref={textareaRef}
        onInput={autoSize}
        onKeyDown={onKeyDown}
        rows={1}
        readOnly={saving}
        defaultValue={content}
        placeholder="Message text"
        autoComplete="off"
        aria-autocomplete="none"
        aria-label="Edit message text"
        className="m-0 max-h-[50vh] w-full resize-none overflow-y-auto rounded-sm border border-zinc-300 bg-white px-3 py-2 text-[15px] leading-[1.375rem] text-zinc-900 outline-hidden focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/40 sm:text-sm dark:border-zinc-700 dark:bg-zinc-900/50 dark:text-zinc-100"
      />
      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={saving}
          className="inline-flex cursor-pointer justify-center rounded-sm border-0 bg-emerald-600 px-3 py-1.5 font-semibold text-sm text-white shadow-xs hover:bg-emerald-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-600 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <span className={twMerge(saving && 'animate-pulse')}>{saveLabel}</span>
        </button>
        <button
          type="button"
          onClick={onClose}
          disabled={saving}
          className="inline-flex cursor-pointer justify-center rounded-sm border-0 bg-transparent px-3 py-1.5 font-semibold text-sm text-zinc-700 ring-1 ring-zinc-300 ring-inset hover:bg-zinc-100 disabled:opacity-50 dark:text-zinc-200 dark:ring-zinc-700 dark:hover:bg-zinc-800"
        >
          Cancel
        </button>
        <span className="ml-auto select-none text-[11px] text-zinc-400 dark:text-zinc-500">
          Esc to cancel · ⌘/Ctrl+Enter to save
        </span>
      </div>
    </form>
  );
}
